import React from 'react';

import {
	BUTTON_TYPES,
	DropdownOptionType,
	EllipsisIcon,
	EvButton,
	EvDropdownMenu,
	EvEllipsedTextWithTooltip,
	EvIcon,
	EvTableColumn,
	EvTooltip,
	GlobeSmallIcon as GlobeIcon,
	IDropdownMenuOption,
	UserSmallIcon as UserIcon
} from '@evinced-private/ui-common';

import { IFilterOptions } from '../../../components/data-filter/DataFilterHelper';
import { isStringMatch } from '../../../helpers/StringHelper';
import {
	CollectionTypeLabels,
	CollectionTypes,
	ICollection,
	ICollectionsTableColumnsPayload,
	ICollectionsTableDefinition
} from '../../../interfaces/Collection';
import { CollectionsPropertiesCell } from '../components/collections-properties-cell/CollectionsPropertiesCell';
import { DeleteCollectionPopup } from '../components/delete-collection-popup/DeleteCollectionPopup';
import { EditCollectionButton } from '../components/edit-collection-button/EditCollectionButton';

import '../ManageCollectionsPage.scss';

const renderTextCell = (text: string, width = 200): JSX.Element => {
	return <EvEllipsedTextWithTooltip text={text} maxWidth={width} showTooltipOnlyOnEllipsed />;
};

const renderNameCell = (
	cell: string,
	row: ICollection,
	{ properties, isAdminUser }: ICollectionsTableColumnsPayload
): JSX.Element => {
	const isEditAllowed = properties?.length && (row.type === CollectionTypes.USER || isAdminUser);

	return isEditAllowed ? (
		<EditCollectionButton
			key={`edit-${row.id}-${row.updatedTime}`}
			collection={row}
			properties={properties}
			type={BUTTON_TYPES.ICON}
			buttonChildren={<div className="name-button">{renderTextCell(cell)}</div>}
			isEditMode={true}
		/>
	) : (
		renderTextCell(cell)
	);
};

const renderCollectionTypesCell = (cell: string, row: ICollection): JSX.Element => {
	const tooltipId = `type-${row.id}`;
	const tooltipLabel =
		row.type === CollectionTypes.USER ? CollectionTypeLabels.USER : CollectionTypeLabels.TENANT;

	return (
		<div className="collection-types-cell">
			<EvButton
				type={BUTTON_TYPES.ICON}
				customTooltip={{ dataTip: tooltipLabel, dataFor: tooltipId }}
				className="icon-wrapper"
				ariaLabel={tooltipLabel}
			>
				<EvIcon icon={row.type === CollectionTypes.USER ? UserIcon : GlobeIcon} small />
			</EvButton>
			<EvTooltip place="bottom" id={tooltipId}>
				{tooltipLabel}
			</EvTooltip>
		</div>
	);
};

const renderActionsCell = (
	cell: string,
	row: ICollection,
	{ properties, isAdminUser }: ICollectionsTableColumnsPayload
): JSX.Element => {
	const isEditAllowed = row.type === CollectionTypes.USER || isAdminUser;
	const isRenderActions = properties.length > 0 && isEditAllowed;

	const options: IDropdownMenuOption[] = [
		{
			title: 'Edit collection',
			id: 'edit-collection',
			type: DropdownOptionType.CUSTOM,
			renderOption: () => (
				<EditCollectionButton
					key={`edit-menu-${row.id}-${row.updatedTime}`}
					collection={row}
					properties={properties}
					type={BUTTON_TYPES.ICON}
					buttonChildren="Edit collection"
					isEditMode={true}
					accessibilityProps={{
						role: 'menuitem'
					}}
				/>
			)
		},
		{
			title: 'Delete collection',
			id: 'delete-collection',
			type: DropdownOptionType.CUSTOM,
			renderOption: () => (
				<DeleteCollectionPopup
					collection={row}
					accessibilityProps={{
						role: 'menuitem'
					}}
				/>
			)
		}
	];

	return (
		<div className="actions-cell">
			{isRenderActions ? (
				<EvDropdownMenu
					id={`menu-${row.id}`}
					options={options}
					triggerButtonProps={{
						type: BUTTON_TYPES.ICON,
						ariaHaspopup: 'menu',
						title: 'Collection Actions',
						children: (
							<div className="menu-trigger-icon">
								<EvIcon icon={EllipsisIcon} small />
							</div>
						)
					}}
				/>
			) : null}
		</div>
	);
};

const getCollectionsTableColumns = ({
	properties,
	isAdminUser
}: ICollectionsTableColumnsPayload): EvTableColumn[] => {
	return [
		{
			dataField: 'name',
			text: 'Name',
			style: { width: '20%' },
			headerStyle: { width: '20%' },
			sort: true,
			formatter: (cell: string, row: ICollection) =>
				renderNameCell(cell, row, { properties, isAdminUser })
		},
		{
			dataField: 'type',
			text: 'Collection Type',
			style: { width: '10%' },
			headerStyle: { width: '10%' },
			formatter: renderCollectionTypesCell
		},
		{
			dataField: 'description',
			text: 'Description',
			style: { width: '28%' },
			headerStyle: { width: '28%' },
			sort: true,
			formatter: (cell: string, row: ICollection) => renderTextCell(row.description, 300)
		},
		{
			dataField: 'properties',
			text: 'Properties',
			style: { width: '32%' },
			headerStyle: { width: '32%' },
			formatter: (cell: string, row: ICollection) => (
				<CollectionsPropertiesCell row={row} properties={properties} />
			)
		},
		{
			dataField: 'actions',
			text: 'Actions',
			style: { width: '10%' },
			classes: 'actions-cell-td',
			headerStyle: { width: '10%' },
			sort: false,
			headerClasses: 'actions-header-cell',
			formatter: (cell: string, row: ICollection) =>
				renderActionsCell(cell, row, { properties, isAdminUser })
		}
	];
};

const getTableData = (
	collections: ICollection[],
	{ searchValue, collectionTypes }: IFilterOptions
): ICollection[] => {
	return collections.filter((collection) => {
		let isSearchMatch = true;
		let isTypeMatch = true;

		if (searchValue) {
			const isNameMatch = isStringMatch(searchValue, collection.name);
			const isDescriptionMatch = isStringMatch(searchValue, collection.description);

			isSearchMatch = isNameMatch || isDescriptionMatch;
		}

		if (collectionTypes.length) {
			isTypeMatch = collectionTypes.reduce((acc, type) => {
				return acc || collection.type === type.value;
			}, false);
		}

		return isSearchMatch && isTypeMatch;
	});
};

export const getTableDefinition = (
	collections: ICollection[],
	appliedFilters: IFilterOptions,
	payload: ICollectionsTableColumnsPayload
): ICollectionsTableDefinition => {
	const result = {
		columns: getCollectionsTableColumns(payload),
		options: {
			title: 'Manage Collections',
			pagination: true,
			dataType: 'collections',
			caption: 'Manage collections table'
		},
		data: appliedFilters ? getTableData(collections, appliedFilters) : [],
		totalCount: collections.length || 0,
		totalPaginationableResults: collections.length || 0
	};

	return result;
};
